import 'twin.macro'

import { Ring } from '@uiball/loaders'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import { TFunction } from 'i18next'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getStatusSelector } from 'redux/store/slices/statusSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { ApplicationFlowTypes, ApplicationSteps, FLOW_STEPS, Nullable, PartialRecord } from 'share/types'
import tw from 'twin.macro'
import Emitter from 'utils/emitter'

type GetWaitingTitleText = {
  flow_type: Nullable<ApplicationFlowTypes> | undefined
  step: Nullable<ApplicationSteps> | undefined
  t: TFunction
}

const getWaitingTitleText = ({ flow_type, step, t }: GetWaitingTitleText) => {
  if (!flow_type || !step) return `${t('flow-please-wait')} !`

  if (flow_type === 'CONFIRMATION_GUARANTOR_INDIVIDUAL') {
    return t('await-guarantor')
  }
  const waitingTexts: PartialRecord<FLOW_STEPS, string | null | undefined> = {
    [FLOW_STEPS.ACQUAINTANCE_GUARANTOR_WITH_DOCUMENTS]: t('await-guarantor'),
    [FLOW_STEPS.SIGN_SPOUSE_DOCS_AITU]: t('await-spouse'),
    [FLOW_STEPS.AWAITING_THIRD_PARTY_CONDITIONS_CONFIRMATION]: t('await_pledger_final_confirmation'),
    [FLOW_STEPS.FETCH_THIRD_PARTY_PLEDGER_DDOCS]: t('await_pledger_id'),
    [FLOW_STEPS.ACQUAINTANCE_THIRD_PARTY_PLEDGER_WITH_DOCUMENTS]: t('await_pledger_signing'),
    [FLOW_STEPS.THIRD_PARTY_PLEDGER_BIOMETRY_FORENSICS]: t('await_pledger_signing'),
    [FLOW_STEPS.SEND_THIRD_PARTY_PLEDGER_OTP_SMS]: t('await_pledger_signing'),
    [FLOW_STEPS.SIGN_DOCS_CERTIFICATION_CENTER_THIRD_PARTY_PLEDGER]: t('await_pledger_signing')
  }

  const text = waitingTexts[step]

  if (!text) return `${t('flow-please-wait')} !`

  return text
}

const InternalStepLoader = () => {
  //Tools
  const { t } = useTranslation()

  // Selectors
  const { current_step, next_step: nextStep } = useAppSelector(getStatusSelector)
  const wsData = useAppSelector(getNotificationsSelector)

  // Computed Values
  const step = current_step?.step
  const flow_type = current_step?.flow_type

  const titleText = useMemo(() => getWaitingTitleText({ flow_type, step, t }), [flow_type, step, t])

  // Effects
  useEffect(() => {
    const checkStatus = async () => {
      if (current_step?.step) {
        const currStepWsData = wsData[current_step.step]
        if (currStepWsData && currStepWsData.value.is_final && currStepWsData.value.is_success) {
          Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
        }
      }
    }
    checkStatus()
  }, [current_step?.step, nextStep, wsData])

  return (
    <ContainerWithSteps>
      <div tw='flex flex-col'>
        <Heading variant='title2' twStyle={tw`text-center`}>
          {titleText}
        </Heading>
        <div tw='flex items-center justify-center my-auto'>
          <Ring size={80} color='var(--primary-color)' />
        </div>
      </div>
    </ContainerWithSteps>
  )
}

export default InternalStepLoader
